import type { IPriceProductField } from '../types';

import {
  PRICE_PRODUCT_PERCENT_SYMBOL,
  PRICE_PRODUCT_QUICK_FILL_CONTROL_GAP,
  PRICE_PRODUCT_QUICK_FILL_OPERATOR,
  PRICE_PRODUCT_QUICK_FILL_SELECT_WRAPPER,
  PRICE_PRODUCT_QUICK_FILL_UNIT,
} from '../constants';
import { usePriceProductTableContext } from '../contexts/PriceProductTableProvider';
import { InlineStack, Select, TextField } from '@shopify/polaris';
import { toQuickFillOperator, toQuickFillUnit } from '../utils';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { useTranslation } from 'react-i18next';

const OPERATOR_OPTIONS = Object.values(PRICE_PRODUCT_QUICK_FILL_OPERATOR).map((operator) => ({
  label: operator,
  value: operator,
}));

interface IProps {
  field: IPriceProductField;
  label: string;
  /** The rule the merchant lands on when the bar opens, so an amount can be typed straight away. */
  isAutoFocused?: boolean;
}

/** One field's rule — `+ 10 %` — read against the Control amount rather than against what is typed. */
export const PriceProductQuickFillRule: React.FC<IProps> = ({ field, label, isAutoFocused }) => {
  const { t } = useTranslation();
  const { currencySymbol } = useConvertMoneyByCurrency();
  const { quickFillRules, handleChangeQuickFillRule } = usePriceProductTableContext();

  const rule = quickFillRules[field];
  // `=` names the amount outright, so there is nothing for a unit to qualify — see the hook, which
  // is what puts the unit back to the shop's currency when the operator lands here.
  const isSetOperator = rule.operator === PRICE_PRODUCT_QUICK_FILL_OPERATOR.SET;

  const unitOptions = [
    { label: PRICE_PRODUCT_PERCENT_SYMBOL, value: PRICE_PRODUCT_QUICK_FILL_UNIT.PERCENT },
    { label: currencySymbol, value: PRICE_PRODUCT_QUICK_FILL_UNIT.CURRENCY },
  ];

  const handleChangeOperator = (value: string) => {
    const operator = toQuickFillOperator(value);
    if (!operator) return;

    handleChangeQuickFillRule({ field, rule: { operator } });
  };

  const handleChangeUnit = (value: string) => {
    const unit = toQuickFillUnit(value);
    if (!unit) return;

    handleChangeQuickFillRule({ field, rule: { unit } });
  };

  return (
    <InlineStack gap={PRICE_PRODUCT_QUICK_FILL_CONTROL_GAP} blockAlign="center" wrap={false}>
      <div {...PRICE_PRODUCT_QUICK_FILL_SELECT_WRAPPER}>
        <Select
          label={t('{{label}} operator', { label: t(label) })}
          labelHidden
          options={OPERATOR_OPTIONS}
          value={rule.operator}
          onChange={handleChangeOperator}
        />
      </div>

      <div className="grow">
        <TextField
          label={t(label)}
          labelHidden
          autoComplete="off"
          inputMode="decimal"
          autoFocus={isAutoFocused}
          placeholder={t('e.g. 10')}
          value={rule.value}
          onChange={(value) => handleChangeQuickFillRule({ field, rule: { value } })}
        />
      </div>

      <div {...PRICE_PRODUCT_QUICK_FILL_SELECT_WRAPPER}>
        <Select
          label={t('{{label}} unit', { label: t(label) })}
          labelHidden
          disabled={isSetOperator}
          options={unitOptions}
          value={rule.unit}
          onChange={handleChangeUnit}
        />
      </div>
    </InlineStack>
  );
};
